import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import JobCard from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { CITIES, WORK_TYPES } from "@/data/categories";

export default function Jobs() {
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ total: 0, jobs: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [showFilters, setShowFilters] = useState(false);

  const category = params.get("category") || "";
  const location = params.get("location") || "";
  const workType = params.get("work_type") || "";

  const load = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams();
    if (params.get("q")) sp.set("q", params.get("q"));
    if (category) sp.set("category", category);
    if (location) sp.set("location", location);
    if (workType) sp.set("work_type", workType);
    try {
      const r = await api.get(`/jobs?${sp.toString()}`);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [params, category, location, workType]);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(params);
    if (val) p.set(key, val); else p.delete(key);
    setParams(p);
  };

  const submitSearch = () => setParam("q", q);
  const activeCat = cats.find((c) => c.slug === category);
  const clearAll = () => { setQ(""); setParams(new URLSearchParams()); };

  const hasFilters = category || location || workType || params.get("q");

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${activeCat ? activeCat.name + " " : ""}İş İlanları | isbul.org`}
        description={`${activeCat ? activeCat.name + " kategorisinde " : ""}güncel iş ilanları. isbul.org ile size uygun işi bulun.`}
        keywords="iş ilanları, iş ara, kariyer, eleman"
        canonical="/ilanlar"
      />
      <Header />

      <div className="bg-gradient-to-b from-blue-50/50 to-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1f222d]">{activeCat ? activeCat.name : "Tüm İş İlanları"}</h1>
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-200 p-2 flex flex-col sm:flex-row gap-2">
            <div className="flex items-center gap-2 flex-1 px-3">
              <Search size={20} className="text-slate-400" />
              <input data-testid="jobs-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitSearch()} placeholder="Pozisyon, şirket ara..." className="w-full py-2.5 outline-none bg-transparent text-[#1f222d]" />
            </div>
            <Button data-testid="jobs-search-btn" onClick={submitSearch} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6">Ara</Button>
            <button data-testid="toggle-filters" onClick={() => setShowFilters((s) => !s)} className="sm:hidden inline-flex items-center justify-center gap-2 py-2.5 border border-slate-200 rounded-xl text-[#1f222d] font-medium">
              <SlidersHorizontal size={18} /> Filtreler
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block space-y-6`}>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1f222d]">Filtrele</h3>
              {hasFilters && <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-blue-600 hover:underline">Temizle</button>}
            </div>

            <div className="mb-5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Kategori</label>
              <select data-testid="filter-category" value={category} onChange={(e) => setParam("category", e.target.value)} className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-400 text-sm text-[#1f222d]">
                <option value="">Tümü</option>
                {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.count})</option>)}
              </select>
            </div>

            <div className="mb-5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Şehir</label>
              <select data-testid="filter-location" value={location} onChange={(e) => setParam("location", e.target.value)} className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-400 text-sm text-[#1f222d]">
                <option value="">Tümü</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Çalışma Türü</label>
              <select data-testid="filter-worktype" value={workType} onChange={(e) => setParam("work_type", e.target.value)} className="w-full mt-2 border border-slate-200 rounded-lg px-3 py-2.5 outline-none focus:border-blue-400 text-sm text-[#1f222d]">
                <option value="">Tümü</option>
                {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
        </aside>

        {/* Results */}
        <main>
          <p className="text-sm text-slate-500 mb-4" data-testid="jobs-count">{loading ? "Yükleniyor..." : `${data.total} ilan bulundu`}</p>
          {loading ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {[...Array(4)].map((_, i) => <div key={i} className="h-44 bg-slate-100 rounded-2xl animate-pulse" />)}
            </div>
          ) : data.jobs.length === 0 ? (
            <div className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-100">
              <X size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-[#1f222d] font-semibold">Sonuç bulunamadı</p>
              <p className="text-sm text-slate-500 mt-1">Farklı filtreler veya anahtar kelimeler deneyin.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {data.jobs.map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
